import { ApplicationInsights, type ICustomProperties } from '@microsoft/applicationinsights-web';

const connectionString = process.env.REACT_APP_APPLICATIONINSIGHTS_CONNECTION_STRING;

let appInsights: ApplicationInsights | null = null;

if (connectionString) {
  appInsights = new ApplicationInsights({
    config: {
      connectionString,
      enableAutoRouteTracking: true,       // Track SPA page views on route change
      autoTrackPageVisitTime: true,        // Track time spent on each page
      enableCorsCorrelation: true,         // Correlate frontend ↔ backend requests
      correlationHeaderExcludedDomains: ['*.auth0.com', '*.sentry.io'],
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      disableFetchTracking: false,         // Track fetch API calls (to your backend)
      enableAjaxPerfTracking: true,        // Track AJAX performance
      maxAjaxCallsPerView: -1,            // Unlimited (track all calls)
    },
  });

  appInsights.loadAppInsights();
  appInsights.context.application.ver = process.env.REACT_APP_VERSION || '1.0.0';
  appInsights.addTelemetryInitializer((item) => {
    if (item.tags) {
      item.tags['ai.cloud.role'] = 'dng-private-app';
    }
  });
}

export { appInsights };

/**
 * Set the authenticated user context (call after login).
 */
export function setAuthenticatedUser(userId: string): void {
  if (!appInsights) return;
  appInsights.setAuthenticatedUserContext(userId, undefined, true);
}

/**
 * Clear user context (call on logout).
 */
export function clearAuthenticatedUser(): void {
  if (!appInsights) return;
  appInsights.clearAuthenticatedUserContext();
}

/**
 * Track a custom event.
 */
export function trackEvent(name: string, properties?: ICustomProperties): void {
  if (!appInsights) return;
  appInsights.trackEvent({ name }, properties);
}

/**
 * Track a custom metric.
 */
export function trackMetric(name: string, average: number, properties?: ICustomProperties): void {
  if (!appInsights) return;
  appInsights.trackMetric({ name, average }, properties);
}

/**
 * Track an exception.
 */
export function trackException(error: Error, properties?: ICustomProperties): void {
  if (!appInsights) return;
  appInsights.trackException({ exception: error }, properties);
}
